import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, DollarSign, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Site settings (availability messages)
  const [siteSettings, setSiteSettings] = useState({ hero_pill: '', pricing_banner: '' });
  const [siteSettingsDraft, setSiteSettingsDraft] = useState({ hero_pill: '', pricing_banner: '' });
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);
  const siteSettingsDirty =
    siteSettings.hero_pill !== siteSettingsDraft.hero_pill ||
    siteSettings.pricing_banner !== siteSettingsDraft.pricing_banner;

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    } else {
      fetchCurrentPricing();
      fetchSiteSettings();
    }
  }, [navigate]);

  const fetchSiteSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/api/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setSiteSettings(res.data.settings);
        setSiteSettingsDraft(res.data.settings);
      }
    } catch (err) {
      console.error('Failed to load site settings', err);
    }
  };

  const handleSaveSiteSettings = async () => {
    setSavingSiteSettings(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.patch(
        `${API}/api/admin/site-settings`,
        siteSettingsDraft,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setSiteSettings(res.data.settings);
        setSiteSettingsDraft(res.data.settings);
        toast.success('Availability messages updated - live on your site now.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save messages');
    } finally {
      setSavingSiteSettings(false);
    }
  };

  const fetchCurrentPricing = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/pricing`);
      if (response.data.success && response.data.packages) {
        setPackages(response.data.packages.map(pkg => ({
          id: pkg.id,
          name: pkg.name,
          price: pkg.price,
          description: pkg.description
        })));
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      // Fallback pricing only on error
      setPackages([
        { id: 'basic', name: 'Essential Collection', price: 100, description: 'Ideal for smaller celebrations and meaningful moments' },
        { id: 'standard', name: 'Signature Collection', price: 150, description: 'Perfect for most occasions - our most popular choice' },
        { id: 'premium', name: 'Cinematic Collection', price: 200, description: 'The complete package for milestone events and weddings' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (id, newPrice) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, price: parseFloat(newPrice) || 0 } : pkg
    ));
    setHasChanges(true);
  };

  const handleSavePricing = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.post(`${API}/api/admin/pricing`, 
        { packages },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Pricing updated successfully! Changes will appear on your website.');
      setHasChanges(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update pricing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-gray-400">Manage your pricing and website configuration</p>
      </div>

      {/* Pricing Settings - Editable */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-black" />
            </div>
            <h2 className="text-xl font-bold text-white">Package Pricing</h2>
          </div>
          {hasChanges && (
            <span className="text-amber-400 text-sm font-medium animate-pulse">
              Unsaved changes
            </span>
          )}
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {loading ? (
              // Loading skeleton
              [1, 2, 3].map((n) => (
                <div key={n} className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 animate-pulse">
                  <div className="h-6 bg-gray-700 rounded mb-4 w-24"></div>
                  <div className="h-16 bg-gray-700 rounded mb-4"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2 w-full"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                </div>
              ))
            ) : (
              packages.map((pkg) => (
              <div key={pkg.id} className="bg-gray-800/50 rounded-lg p-6 border border-gray-700 hover:border-amber-500 transition-all">
                <h3 className="text-white font-semibold mb-4 text-lg">{pkg.name}</h3>
                
                <div className="mb-4">
                  <label className="block text-gray-400 text-sm mb-2">Price (£)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl">£</span>
                    <input
                      type="number"
                      value={pkg.price}
                      onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-900 text-white text-2xl font-bold rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/50 outline-none"
                      min="0"
                      step="5"
                    />
                  </div>
                </div>

                <p className="text-gray-400 text-sm">{pkg.description}</p>
                
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="text-xs text-gray-500">Booking Deposit</div>
                  <div className="text-amber-400 font-bold">£{({basic: 20, standard: 30, premium: 50}[(pkg.id || pkg.name || '').toLowerCase()] ?? Math.round((pkg.price * 0.25) / 5) * 5).toFixed(2)}</div>
                </div>
              </div>
            ))
            )}
          </div>

          <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
            <p className="text-cyan-400 text-sm">
              <strong>💡 Tip:</strong> Update your package prices here and click "Save Changes". 
              The new prices will be displayed on your website pricing page immediately.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSavePricing}
              disabled={!hasChanges || loading}
              className={`inline-flex items-center px-8 py-3 rounded-lg font-semibold transition-all ${
                hasChanges && !loading
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-amber-500/50'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>

            {hasChanges && !loading && (
              <button
                onClick={() => {
                  setPackages([
                    { id: 'basic', name: 'Essential Collection', price: 100, description: 'Ideal for smaller celebrations and meaningful moments' },
                    { id: 'standard', name: 'Signature Collection', price: 150, description: 'Perfect for most occasions - our most popular choice' },
                    { id: 'premium', name: 'Cinematic Collection', price: 200, description: 'The complete package for milestone events and weddings' }
                  ]);
                  setHasChanges(false);
                  toast.info('Changes discarded');
                }}
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              >
                Discard Changes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Availability Messages */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6" data-testid="availability-messages-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Megaphone size={20} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Availability Messages</h2>
          </div>
          {siteSettingsDirty && (
            <span className="text-amber-400 text-sm font-medium animate-pulse">Unsaved changes</span>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-gray-300 text-sm mb-2">
              Hero pill <span className="text-gray-500">(homepage, top of hero)</span>
            </label>
            <input
              type="text"
              maxLength={120}
              value={siteSettingsDraft.hero_pill}
              onChange={(e) => setSiteSettingsDraft({ ...siteSettingsDraft, hero_pill: e.target.value })}
              data-testid="site-settings-hero-pill"
              className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none"
              placeholder="e.g. Now booking weddings & memorials - Spring 2026"
            />
            <p className="text-xs text-gray-500 mt-1">{(siteSettingsDraft.hero_pill || '').length}/120 characters</p>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">
              Pricing urgency banner <span className="text-gray-500">(shown above the order form once a package is selected)</span>
            </label>
            <textarea
              rows={2}
              maxLength={220}
              value={siteSettingsDraft.pricing_banner}
              onChange={(e) => setSiteSettingsDraft({ ...siteSettingsDraft, pricing_banner: e.target.value })}
              data-testid="site-settings-pricing-banner"
              className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none resize-none"
              placeholder="e.g. Limited weekend slots - secure your date with a small booking deposit."
            />
            <p className="text-xs text-gray-500 mt-1">{(siteSettingsDraft.pricing_banner || '').length}/220 characters</p>
            <p className="text-xs text-amber-400/80 mt-1" data-testid="site-settings-banner-refund-note">
              A "Cancellations &amp; refunds" link is added after this text automatically. Please don't add refund promises here, so the wording stays consistent with the Terms.
            </p>
          </div>

          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
            <p className="text-purple-300 text-sm">
              <strong>💡 Tip:</strong> Use these to create seasonal urgency. Example rotations: <em>"Now booking 2026 weddings"</em>, <em>"Christmas memorial tributes booking now"</em>, <em>"Only 3 weekend slots left this month"</em>.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSaveSiteSettings}
              disabled={!siteSettingsDirty || savingSiteSettings}
              data-testid="site-settings-save-btn"
              className={`inline-flex items-center px-8 py-3 rounded-lg font-semibold transition-all ${
                siteSettingsDirty && !savingSiteSettings
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-purple-500/50'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {savingSiteSettings ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} className="mr-2" />
                  Save Messages
                </>
              )}
            </button>

            {siteSettingsDirty && !savingSiteSettings && (
              <button
                onClick={() => setSiteSettingsDraft(siteSettings)}
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              >
                Discard Changes
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Current Website Preview */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6">
        <h2 className="text-xl font-bold text-white mb-4">Current Live Prices</h2>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-gray-400 mb-1">Essential Collection</div>
            <div className="text-3xl font-bold text-white">£{packages.find(p => p.id === 'basic')?.price || 100}</div>
          </div>
          <div>
            <div className="text-gray-400 mb-1">Signature Collection</div>
            <div className="text-3xl font-bold text-amber-400">£{packages.find(p => p.id === 'standard')?.price || 150}</div>
          </div>
          <div>
            <div className="text-gray-400 mb-1">Cinematic Collection</div>
            <div className="text-3xl font-bold text-white">£{packages.find(p => p.id === 'premium')?.price || 70}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
